import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "./motion-primitives";
import { CtaButton } from "./CtaButton";
import { IMAGES } from "./data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      {/* Parallax image layer */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Lantern-lit street in Vietnam at evening"
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Editorial gradient overlays (no flat black blanket) */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-16 sm:px-10 lg:px-16 lg:pb-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 flex items-center gap-4"
        >
          <span className="h-px w-10 bg-teal" />
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-cream/80">
            Curated, not crowdsourced
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="max-w-5xl font-serif text-[2.4rem] font-light leading-[1.03] tracking-tight text-cream sm:text-5xl lg:text-[4.4rem]"
        >
          <MaskedLines
            delay={0.35}
            lines={[
              "Travel like the",
              <>
                local-with-<span className="italic text-teal">good&nbsp;taste</span>
              </>,
              "you wish you knew.",
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.9 }}
          className="mt-6 max-w-xl font-sans text-base leading-relaxed text-cream/80 sm:text-lg"
        >
          Personal recommendations and thoughtfully planned holidays. We help bring together stays,
          experiences and travel arrangements around how you want to travel.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9 }}
          className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <CtaButton testId="hero-plan-trip-cta" to="/plan-my-trip">
            Plan My Trip
          </CtaButton>
          <CtaButton testId="hero-discoverography-cta" variant="secondary" to="/discoverography">
            Explore Discoverography
          </CtaButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 0.9 }}
          data-testid="hero-support-line"
          className="mt-5 font-sans text-sm text-cream/60"
        >
          Share your plans. A real person will help shape what comes next.
        </motion.p>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 sm:right-10 lg:right-16 lg:flex"
      >
        <span className="font-sans text-[0.65rem] uppercase tracking-[0.3em] text-cream/50">
          Scroll
        </span>
        <span className="relative h-10 w-px overflow-hidden bg-cream/20">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0 block bg-teal"
          />
        </span>
      </motion.div>
    </section>
  );
};
